import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Button } from 'semantic-ui-react';
import { Link } from 'nallian-shared-ui/lib/components';

const Container = styled.div`
  margin-bottom: 1rem;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
  color: rgba(0, 0, 0, 0.87);
`;

const Subtitle = styled.div`
  font-size: 0.875rem;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.54);
  margin: 0 0 1rem 0;
`;

const NavigationSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  background-color: #d9edf7;
  padding: 0.5rem;
  width: calc(100% + 2rem);
  margin-left: -1rem;
  margin-right: -1rem;
`;

const Separator = styled.span`
  color: rgba(0, 0, 0, 0.4);
  font-size: 0.8rem;
  user-select: none;
  display: flex;
  align-items: center;
  line-height: 1;
`;

const NavButton = styled(Button)`
  &&& {
    text-align: left;
    padding: 0.125rem 0.375rem;
    font-size: 0.8rem;
    height: auto;
    line-height: 1;
    border-radius: 3px;
  }
`;

const ContextualTitle = ({ appInstallation, feature, role, appName, appId, installationId }) => {
  const { t } = useTranslation();

  // Priority: role > feature > appInstallation
  let title = '';
  let subtitle = '';

  if (role) {
    title = role.name;
    subtitle = t('roleInFeature', { featureName: feature?.name });
  } else if (feature) {
    title = feature.name;
    subtitle = t('featureInInstallation', { installationName: appInstallation?.name });
  } else if (appInstallation) {
    title = appInstallation.name;
    subtitle = t('installationOfApp', { appName });
  } else {
    title = appName || t('app');
    subtitle = '';
  }

  const navigationItems = useMemo(() => {
    if (!role && !feature && !appInstallation) return null;

    const items = [];

    // Add installation navigation if we have installation context
    if (appInstallation && appId && installationId) {
      items.push(
        <NavButton
          key="installation"
          basic
          as={Link}
          to={`/apps/${appId}/installations/${installationId}`}
        >
          <strong>{t('installation')}:</strong> {appInstallation.name}
        </NavButton>
      );
    }

    // Add feature navigation if we have feature context
    if (feature && appId && installationId) {
      if (items.length > 0) items.push(<Separator key="sep1">•</Separator>);
      items.push(
        <NavButton
          key="feature"
          basic
          as={Link}
          to={`/apps/${appId}/installations/${installationId}/features/${feature.id}`}
        >
          <strong>{t('feature')}:</strong> {feature.name}
        </NavButton>
      );
    }

    // Add role navigation if we have role context
    if (role) {
      if (items.length > 0) items.push(<Separator key="sep2">•</Separator>);
      items.push(
        <NavButton
          key="role"
          basic
          as={Link}
          to={`/apps/${appId}/installations/${installationId}/roles/${role.id}`}
        >
          <strong>{t('role')}:</strong> {role.name}
        </NavButton>
      );
    }

    return items.length > 0 ? items : null;
  }, [role, feature, appInstallation, appId, installationId, t]);

  return (
    <Container>
      <Title>{title}</Title>
      {subtitle && <Subtitle>{subtitle}</Subtitle>}
      {navigationItems && <NavigationSection>{navigationItems}</NavigationSection>}
    </Container>
  );
};

ContextualTitle.propTypes = {
  appInstallation: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  feature: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  role: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  appName: PropTypes.string,
  appId: PropTypes.string,
  installationId: PropTypes.string,
};

ContextualTitle.defaultProps = {
  appInstallation: null,
  feature: null,
  role: null,
  appName: '',
  appId: null,
  installationId: null,
};

export default ContextualTitle;
